import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { shortText } from '../print/rules';
import type { Saver } from '../sync/saver';
import { isThaiText } from '../thai/ThaiTextbox';
import { drawBlack, drawLayers, type Layer } from '../video/compose';
import { videoConvert } from '../video/engine';
import { dipAt, levelsAt, voiceLevelAt } from '../video/fades';
import { initVideoVoice, syncSubs } from './videoVoice';
import { renderLayers, type PictureCache } from '../video/overlays';
import { createUrlCache, deleteMedia, getMedia, hasMedia, keepStorage, mediaErrorText, putMedia } from '../video/media';
import { createPlayer } from '../video/player';
import {
  checkTiming, clipAt, clipLength, clipStarts, CLIP_MIN_SECONDS, fmtTime, moveClip, newId, newTiming, OVERLAY_MIN_SECONDS, OVERLAY_NEW_SECONDS, removeClip,
  setClipVolume, setEndFades, setJointFade, setMusic, splitClip, NAME_MAX_CHARS, timingOf, totalLength, trimClip,
  type Edit, type OverlayTiming, type VideoClip, type VideoProject, emptySlots, isEmptySlot, checkVideo, fitVoices
} from '../video/project';
import { $, $in, setPressed } from './dom';
import { closeSheets, isOpen, openSheet } from './sheets';
import { toast } from './toast';
import { initVideoExport } from './videoExport';
import { initVideoImport } from './videoImport';
import { initVideoSlots } from './videoSlots';
import { preparePhoto } from '../video/photo';
import { initMusicLibrary } from './musicLibrary';
import { api, type MusicInfo, type Perm } from '../sync/api';
import { listMedia } from '../video/media';

/*
 * The video screen (board 22): preview, timeline with the playhead fixed in the middle (scroll the timeline to move
 * through the video), and the tools: + คลิป, แบ่ง, ตัดหัวท้าย, ย้าย ‹ ›, เสียง, ลบ.
 * A tool works on the selected clip, or on the clip under the playhead when none is selected.
 * Every change is one undo step through history.setVideo() and is saved like any design.
 * Clips not on this device are named in a red bar (board 24) with a way to put them back from the original file.
 *
 * V2: "ข้อความ" opens the design screen over a still of the video (the work's own objects are the text/logos/pictures);
 * each one shows on the timeline row "ข้อความ/รูป" and, when chosen there, gets its start/end from the playhead.
 * "จางหาย" sets the fades (src/video/fades.ts). The preview draws both over the clip (paintOver), like the export.
 */

export interface VideoCtx {
  ed: Editor; saver: Saver;
  project: () => VideoProject | null;
  readonly: () => boolean;
  missing: () => Set<string>;
  /** apply an edit (one undo step); shows the reason and returns false when it cannot be done */
  commit: (e: Edit) => boolean;
  /** the text/pictures as pictures for the export, drawn fresh if the work changed (throws with a Thai reason) */
  overlays: () => Promise<Layer[]>;
  /** the ones last drawn (for the checks before exporting) */
  overlaysShown: () => Layer[];
  /** the whole work as JSON now (to tell whether an exported file still shows it) */
  workJson: () => string;
}

const PX_PER_SECOND = 40;
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);

/** `tpl`: templates (admin) — may this person publish one, and open "บันทึกเป็นเทมเพลต" */
export function initVideoView(ed: Editor, history: History, saver: Saver,
  tpl: { canPublish: () => boolean; can: (p: Perm) => boolean; isAdmin: () => boolean; publish: () => void }) {
  const app = document.querySelector('.app')!;
  const view = $('vview'), tl = $('vTl'), tlIn = $('vTlIn'), clipsRow = $('vClips'), musicRow = $('vMusic');
  const urls = createUrlCache();
  let missing = new Set<string>();
  let selected: string | null = null;      // clip id
  let shown: VideoProject | null = null;   // the project the timeline was drawn from
  let checkRun = 0;
  const thumbs = new Map<string, string>(); // media key → small picture

  const project = () => ed.doc.video ?? null;
  const readonly = () => !!saver.viewing;

  const ctx: VideoCtx = {
    ed, saver, project, readonly, missing: () => missing,
    commit(e: Edit) {
      if (!e.ok) { toast(e.error, true); return false; }
      if (readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อนจึงจะแก้ได้', true); return false; }
      // v4: every scene keeps room for its voice (1ก), and the subtitles follow the scenes (6ก) — in the same undo step
      const p = fitVoices(e.project);
      const pre = checkVideo(p);
      if (!pre.ok) { toast('แก้แบบนี้ไม่ได้: ' + pre.error, true); return false; }
      if (!history.loading) history.silently(() => syncSubs(ed, p));
      const why = history.setVideo(p);
      if (why) { toast(why, true); return false; }
      void sync();
      return true;
    },
    overlays: () => currentLayers(),
    overlaysShown: () => layers,
    workJson: () => history.snapshot()
  };

  /* ---------- preview ---------- */
  const player = createPlayer($('vFrame'), k => missing.has(k) ? Promise.resolve(null) : urls.url(k), {
    onTime: t => {
      const p = project();
      $('vTime').textContent = `${fmtTime(t)} / ${fmtTime(p ? totalLength(p) : 0)}`;
      if (player.playing) setScroll(t);
      showGone(t);
      paintOver(t);
    },
    onPlayState: on => {
      // the icons are swapped by hiding one, never rebuilt under the finger (iPad stops taking taps)
      $('vPlay').setAttribute('aria-label', on ? 'หยุดชั่วคราว' : 'เล่น');
      $('vPlayIcon').toggleAttribute('hidden', on);
      $('vPauseIcon').toggleAttribute('hidden', !on);
    },
    onProblem: reason => toast(reason, true),
    levels: t => { const p = project(); return p ? levelsAt(p, t) : { clip: 1, music: 1 }; },
    voiceLevel: t => { const p = project(); return p ? voiceLevelAt(p, t) : 1; }
  });
  $('vPlay').onclick = () => { if (player.playing || player.starting) player.pause(); else void player.play(); };

  /** a clip that is not on this device: hatched picture with its name instead of a frozen frame */
  function showGone(t: number) {
    const p = project(), gone = $('vGone');
    const at = p ? clipAt(p, t) : null;
    const c = at ? p!.clips[at.index] : null;
    const empty = !!c && isEmptySlot(c);
    gone.hidden = !c || (!empty && !missing.has(c.media));
    gone.classList.toggle('vgslot', empty);
    if (c && !gone.hidden) {
      const text = empty
        ? `ช่องที่ ${at!.index + 1}: ${c.slot?.label ?? ''}${c.slot?.hint ? `\n${c.slot.hint}` : ''}\n${clipLength(c).toFixed(1)} วิ · กด "ช่องคลิป" เพื่อเติม`
        : `คลิป ${c.name} ไม่อยู่ในเครื่องนี้`;
      if (gone.textContent !== text) gone.textContent = text;
    }
  }

  /* ---------- timeline: scroll position = time under the playhead ---------- */
  let programmatic = false;
  let scrubRaf = 0;
  function pad() { const half = Math.round(tl.clientWidth / 2); tlIn.style.paddingInline = `${half}px`; }
  function setScroll(t: number) {
    programmatic = true;
    tl.scrollLeft = t * PX_PER_SECOND;
    requestAnimationFrame(() => { programmatic = false; });
  }
  tl.addEventListener('scroll', () => {
    if (programmatic) return;
    if (player.playing) player.pause();
    cancelAnimationFrame(scrubRaf);
    scrubRaf = requestAnimationFrame(() => void player.seek(tl.scrollLeft / PX_PER_SECOND));
  }, { passive: true });
  new ResizeObserver(() => {
    pad(); setScroll(player.time);
    view.style.setProperty('--vtl-h', `${Math.max(60, tl.offsetHeight - 6)}px`);   // the playhead line spans every row
  }).observe(tl);

  function clipLabel(k: number, c: VideoClip) {
    if (isEmptySlot(c)) return `${k + 1} · ＋ ${c.slot?.label ?? 'ช่องว่าง'} · ${clipLength(c).toFixed(1)} วิ`;
    return `${k + 1} · ${c.kind === 'photo' ? 'รูป · ' : ''}${fmtTime(clipLength(c))}${c.hold > 0.05 ? ` · ค้าง ${c.hold.toFixed(1)}` : ''}`;
  }

  function drawTimeline(p: VideoProject) {
    shown = p;
    clipsRow.textContent = '';
    p.clips.forEach((c, k) => {
      const b = document.createElement('button');
      const last = p.clips.length - 1;
      b.className = 'vclip' + (isEmptySlot(c) ? ' vslotc' : missing.has(c.media) ? ' vlost' : '')
        + ((k === 0 ? p.fadeIn : c.fade) ? ' vfin' : '') + ((k === last ? p.fadeOut : p.clips[k + 1].fade) ? ' vfout' : '');
      b.dataset.id = c.id;
      b.style.width = `${Math.max(1, clipLength(c) * PX_PER_SECOND - 2)}px`;   // + the 2px gap = exactly the clip's time
      b.setAttribute('aria-label', isEmptySlot(c)
        ? `ช่องว่างที่ ${k + 1} ${c.slot?.label ?? ''} ยาว ${fmtTime(clipLength(c))}`
        : `${c.kind === 'photo' ? 'รูป' : 'คลิป'}ที่ ${k + 1} ${c.name} ยาว ${fmtTime(clipLength(c))}${missing.has(c.media) ? ' ไม่อยู่ในเครื่องนี้' : ''}`);
      const s = document.createElement('span'); s.textContent = clipLabel(k, c);
      b.append(s);
      const th = thumbs.get(c.media);
      if (th) b.style.backgroundImage = `url("${th}")`;
      b.onclick = () => { selected = selected === c.id ? null : c.id; oSel = null; markSelected(); paintOTools(); };
      clipsRow.append(b);
    });
    musicRow.textContent = '';
    if (p.music) {
      const m = document.createElement('div');
      m.className = 'vmus' + (missing.has(p.music.media) ? ' vlost' : '');
      m.style.width = `${Math.max(40, Math.min(p.music.dur, Math.max(totalLength(p), 1)) * PX_PER_SECOND)}px`;
      m.textContent = `${p.music.name} · เสียง ${Math.round(p.music.volume * 100)}%${missing.has(p.music.media) ? ' · ไม่อยู่ในเครื่องนี้' : ''}`;
      musicRow.append(m);
    } else {
      const e = document.createElement('span'); e.className = 'vmusempty'; e.textContent = 'ไม่มีเพลง · กด "เสียง" เพื่อเลือกเพลง';
      musicRow.append(e);
    }
    if (selected && !p.clips.some(c => c.id === selected)) selected = null;
    markSelected();
    drawOverlayRow(p);
    $('vEmpty').hidden = p.clips.length > 0;
    const none = !p.clips.length;
    for (const id of ['vSplit', 'vTrim', 'vLeft', 'vRight', 'vDel']) ($(id) as HTMLButtonElement).disabled = none;
    ($('vFade') as HTMLButtonElement).disabled = none;
    pad();
  }
  function markSelected() {
    clipsRow.querySelectorAll<HTMLElement>('.vclip').forEach(b => b.classList.toggle('vsel', b.dataset.id === selected));
  }

  /** load the small pictures of the clips (once per media key) */
  async function loadThumbs(p: VideoProject) {
    for (const c of p.clips) {
      if (isEmptySlot(c) || thumbs.has(c.media) || missing.has(c.media)) continue;
      try { const m = await getMedia(c.media); thumbs.set(c.media, m?.thumb ?? ''); } catch { thumbs.set(c.media, ''); }
    }
  }

  /* ---------- which files are on this device ---------- */
  async function checkMissing(p: VideoProject): Promise<string | null> {
    const keys = [...p.clips.filter(c => !isEmptySlot(c)).map(c => c.media), ...(p.music ? [p.music.media] : [])];
    try {
      const have = await hasMedia(keys);
      missing = new Set(keys.filter(k => !have.has(k)));
      return null;
    } catch (e) {
      missing = new Set(keys);
      return mediaErrorText(e);
    }
  }

  /** change a text only when it differs (the red bar is an alert: screen readers repeat it on every change) */
  const setText = (id: string, text: string) => { const el = $(id); if (el.textContent !== text) el.textContent = text; };

  function paintMissing(p: VideoProject, storeError: string | null) {
    const lostClips = p.clips.filter(c => !isEmptySlot(c) && missing.has(c.media));
    const names = [...new Map(lostClips.map(c => [c.media, c])).values()];
    const musicLost = !!p.music && missing.has(p.music.media);
    const box = $('vMiss');
    box.hidden = !names.length && !musicLost;
    if (box.hidden) { relinkPending = null; return; }
    if (relinkPending || ($('vRelink') as HTMLButtonElement).disabled) return;   // keep the question or progress on screen
    const parts = [];
    if (lostClips.length) parts.push(`${lostClips.length} คลิป`);
    if (musicLost) parts.push('เพลงประกอบ');
    setText('vMissTitle', `${parts.join(' และ ')}ไม่อยู่ในเครื่องนี้`);
    setText('vMissText', storeError
      ? `เปิดที่เก็บคลิปในเครื่องไม่ได้ (${storeError})`
      : readonly()
        ? 'คลิปเก็บอยู่ในเครื่องของเจ้าของงาน คนที่ได้รับแชร์จึงเห็นแค่รายการคลิป ขอไฟล์ MP4 ที่ส่งออกแล้วจากเจ้าของงานแทน'
        : 'คลิปถูกเก็บไว้ในเครื่องที่นำเข้า หรือเบราว์เซอร์ล้างข้อมูลเว็บไปแล้ว ส่งออกไม่ได้จนกว่าจะใส่กลับ');
    $('vRelink').hidden = readonly() || !!storeError;
    setText('vMissNote', (names.length ? 'ไฟล์ที่ต้องใช้: ' + names.map(c => `${c.name} (ยาว ${fmtTime(c.srcDur)})`).join(', ') : '')
      + (musicLost ? `${names.length ? ' · ' : ''}เพลง: ${p.music!.name} (ยาว ${fmtTime(p.music!.dur)})` : '')
      + (readonly() ? '' : ' · กันคลิปหาย: กด "เพิ่มไปยังหน้าจอโฮม" แล้วเปิดแอปจากไอคอน'));
  }

  /** after any load, undo/redo, edit or view change; a failure is shown, never dropped */
  async function sync() {
    try { await syncNow(); }
    catch (e) { toast('แสดงงานวิดีโอไม่ครบ: ' + errText(e), true); }
  }
  let workShown = -1;   // saver.openedCount of the work on screen: another work → forget the old one's files and results
  async function syncNow() {
    const p = project();
    const run = ++checkRun;
    app.classList.toggle('video', !!p);
    if (!p || workShown !== saver.openedCount) stopEditing();   // another work (or a design) was opened
    view.hidden = !p || editing;
    if (workShown !== saver.openedCount) {
      workShown = saver.openedCount;
      player.pause(); urls.revoke(); thumbs.clear(); missing = new Set(); selected = null; oSel = null;
      layers = []; layersKey = ''; failedKey = ''; layerRun++;
      over.getContext('2d')?.clearRect(0, 0, over.width, over.height);   // nothing of the previous work stays on screen
      exporter.clear();
      relinkPending = null; $('vRelinkUse').hidden = true; $('vMissNote').textContent = '';
      voice.stop();
    }
    if (!p) {
      if (shown) { shown = null; await player.setProject(null); }
      return;
    }
    $('vFrame').style.setProperty('--v-ratio', String(ed.doc.w / ed.doc.h));
    // someone else's video: the read-only bar sits at the top of the work area, so the video screen starts below it
    view.style.top = readonly() ? `${$('viewbar').offsetHeight}px` : '';
    const storeError = await checkMissing(p);
    if (run !== checkRun) return;
    fetchLibraryMusic(p, storeError);
    await loadThumbs(p);
    if (run !== checkRun) return;
    drawTimeline(p);
    paintOTools();
    paintMissing(p, storeError);
    await player.setProject(p);
    if (run !== checkRun) return;
    setScroll(player.time);
    showGone(player.time);
    paintOver(player.time);
    if (editing) refreshStill(p);
    await refreshLayers();   // text/pictures drawn after the clips show (fonts or pictures may take a while)
    if (run !== checkRun) return;
    if (isOpen('shVSound')) paintSound();
    if (isOpen('shVFade')) paintFade();
    voice.sync();
    slots.sync();
    paintSlotsBtn(p);
    if (isOpen('shVExport')) exporter.paint();
  }

  /* ---------- tools ---------- */

  /** the selected clip, or the one under the playhead */
  function target(): number {
    const p = project(); if (!p || !p.clips.length) return -1;
    const k = selected ? p.clips.findIndex(c => c.id === selected) : -1;
    return k >= 0 ? k : clipAt(p, player.time)?.index ?? -1;
  }
  function editable(): boolean {
    if (readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อนจึงจะแก้ได้', true); return false; }
    return !!project();
  }

  $('vSplit').onclick = () => {
    const p = project(); if (!editable() || !p) return;
    player.pause();
    const at = clipAt(p, player.time);
    if (!at) return;
    const id = newId();
    if (ctx.commit(splitClip(p, at.index, at.local, id))) { selected = id; markSelected(); toast(`แบ่งคลิปที่ ${at.index + 1} ที่ ${fmtTime(player.time)} แล้ว`); }
  };
  $('vDel').onclick = () => {
    const p = project(); if (!editable() || !p) return;
    const k = target(); if (k < 0) return;
    player.pause();
    if (ctx.commit(removeClip(p, k))) { selected = null; toast(`ลบคลิปที่ ${k + 1} แล้ว กดเลิกทำถ้าต้องการกลับไป`); }
  };
  for (const [id, dir] of [['vLeft', -1], ['vRight', 1]] as const) {
    $(id).onclick = () => {
      const p = project(); if (!editable() || !p) return;
      const k = target(); if (k < 0) return;
      player.pause();
      const c = p.clips[k];
      if (ctx.commit(moveClip(p, k, dir))) {
        selected = c.id; markSelected();
        const np = project()!;
        void player.seek(clipStarts(np)[k + dir]);
      }
    };
  }

  /* ---------- ตัดหัวท้าย (within the prepared copy) ---------- */
  const tIn = $in('vtIn'), tOut = $in('vtOut'), tVideo = $<HTMLVideoElement>('vtVideo');
  /** `moved*`: which end the person changed; an end left alone keeps its exact value (a slider rounds what it is given) */
  let trimming: { id: string; copyDur: number; in: number; out: number; movedIn: boolean; movedOut: boolean } | null = null;
  $('vTrim').onclick = async () => {
    const p = project(); if (!editable() || !p) return;
    const k = target(); if (k < 0) return;
    player.pause();
    const c = p.clips[k];
    if (missing.has(c.media)) { toast(`คลิปที่ ${k + 1} ไม่อยู่ในเครื่องนี้ ใส่คลิปกลับก่อนจึงจะตัดได้`, true); return; }
    trimming = { id: c.id, copyDur: c.copyDur, in: c.in, out: c.out, movedIn: false, movedOut: false };
    selected = c.id; markSelected();
    for (const r of [tIn, tOut]) { r.max = String(c.copyDur); r.step = 'any'; }
    tIn.value = String(c.in); tOut.value = String(c.out);
    $('vtTitle').textContent = `ตัดหัวท้าย · คลิปที่ ${k + 1}`;
    $('vtMsg').hidden = true;
    paintTrim();
    try {
      const url = await urls.url(c.media);
      if (url) { tVideo.src = url; tVideo.currentTime = c.in; }
    } catch (e) { $('vtMsg').textContent = 'แสดงตัวอย่างคลิปไม่ได้ (ยังตัดได้): ' + mediaErrorText(e); $('vtMsg').hidden = false; }
    openSheet('shVTrim');
  };
  const trimIn = () => trimming!.movedIn ? Number(tIn.value) : trimming!.in;
  const trimOut = () => trimming!.movedOut ? Number(tOut.value) : trimming!.out;
  function paintTrim() {
    if (!trimming) return;
    const a = trimIn(), b = trimOut();
    $('vtInV').textContent = fmtTime(a); $('vtOutV').textContent = fmtTime(b);
    const p = project(), c = p?.clips.find(x => x.id === trimming!.id);
    $('vtInfo').textContent = `ใช้ ${(b - a).toFixed(1)} วิ จากคลิปที่เตรียมไว้ ${trimming.copyDur.toFixed(1)} วิ`
      + (c ? ` (ช่วง ${fmtTime(c.srcStart)}–${fmtTime(c.srcEnd)} ของ ${c.name})` : '') + ' · ถ้าต้องการส่วนนอกช่วงนี้ ต้องนำเข้าคลิปใหม่';
  }
  tIn.oninput = () => {
    if (trimming) trimming.movedIn = true;
    if (Number(tIn.value) > trimOut() - CLIP_MIN_SECONDS) tIn.value = String(Math.max(0, trimOut() - CLIP_MIN_SECONDS));
    if (tVideo.readyState >= 1) tVideo.currentTime = Number(tIn.value);
    paintTrim();
  };
  tOut.oninput = () => {
    if (trimming) trimming.movedOut = true;
    if (Number(tOut.value) < trimIn() + CLIP_MIN_SECONDS) tOut.value = String(Math.min(trimming?.copyDur ?? 0, trimIn() + CLIP_MIN_SECONDS));
    if (tVideo.readyState >= 1) tVideo.currentTime = Number(tOut.value);
    paintTrim();
  };
  $('vtApply').onclick = () => {
    const p = project(); if (!trimming || !p) return;
    const k = p.clips.findIndex(c => c.id === trimming!.id);
    if (k < 0) { $('vtMsg').textContent = 'ไม่พบคลิปนี้แล้ว (อาจถูกลบหรือเลิกทำไป)'; $('vtMsg').hidden = false; return; }
    const e = trimClip(p, k, trimIn(), trimOut());
    if (!e.ok) { $('vtMsg').textContent = e.error; $('vtMsg').hidden = false; return; }
    if (ctx.commit(e)) { closeSheets(); tVideo.removeAttribute('src'); void player.seek(clipStarts(project()!)[k]); toast(`ตัดคลิปที่ ${k + 1} เหลือ ${fmtTime(clipLength(project()!.clips[k]))} แล้ว`); }
  };

  /* ---------- เสียงและเพลง ---------- */
  const sClip = $in('vsClip'), sMusic = $in('vsMusic');
  let soundClip: string | null = null;
  let musicBusy = false;      // a music file is being read and stored
  let relinkBusy = false;     // clips are being cut again from an original file
  function paintSound() {
    const p = project(); if (!p) return;
    const k = soundClip ? p.clips.findIndex(c => c.id === soundClip) : -1;
    const c = k >= 0 ? p.clips[k] : null;
    $('vsClipTitle').textContent = c ? `เสียงของคลิปที่ ${k + 1} (${shortText(c.name, 30)})` : 'เสียงของคลิป (ยังไม่มีคลิป)';
    sClip.disabled = !c || !c.hasAudio || readonly();
    sClip.value = String(Math.round((c?.volume ?? 1) * 100));
    $('vsClipV').textContent = c ? `${Math.round(c.volume * 100)}%` : '';
    $('vsClipNote').textContent = c && !c.hasAudio ? 'คลิปนี้ไม่มีเสียง' : 'เลือกคลิปอื่นบนไทม์ไลน์ก่อนเปิดหน้านี้ เพื่อปรับเสียงของคลิปนั้น';
    $('vsMusicOn').hidden = !p.music;
    $('vsMusicInfo').textContent = p.music
      ? `${p.music.name} · ยาว ${fmtTime(p.music.dur)}${missing.has(p.music.media) ? ' · ไม่อยู่ในเครื่องนี้' : ''}`
      : 'ยังไม่มีเพลง เลือกไฟล์เพลง (mp3, m4a, wav) จากเครื่อง เพลงเริ่มพร้อมวิดีโอ';
    sMusic.value = String(Math.round((p.music?.volume ?? 0.6) * 100));
    $('vsMusicV').textContent = p.music ? `${Math.round(p.music.volume * 100)}%` : '';
    $('vsPick').textContent = p.music ? 'เปลี่ยนเพลง' : 'เลือกไฟล์เพลงจากเครื่อง';
    ($('vsRemove') as HTMLButtonElement).hidden = !p.music;
    ($('vsPick') as HTMLButtonElement).disabled = readonly();
    ($('vsRemove') as HTMLButtonElement).disabled = readonly();
    sMusic.disabled = readonly();
  }
  const sayS = (t: string) => { $('vsMsg').textContent = t; $('vsMsg').hidden = !t; };
  $('vSound').onclick = () => {
    const p = project(); if (!p) return;
    player.pause();
    const k = target();
    soundClip = k >= 0 ? p.clips[k].id : null;
    sayS(''); paintSound(); openSheet('shVSound');
  };
  sClip.oninput = () => { $('vsClipV').textContent = `${sClip.value}%`; };
  sClip.onchange = () => {
    const p = project(); if (!p || !soundClip) return;
    const k = p.clips.findIndex(c => c.id === soundClip);
    if (k >= 0) ctx.commit(setClipVolume(p, k, Number(sClip.value) / 100));
  };
  sMusic.oninput = () => { $('vsMusicV').textContent = `${sMusic.value}%`; };
  sMusic.onchange = () => {
    const p = project(); if (!p?.music) return;
    ctx.commit(setMusic(p, { ...p.music, volume: Number(sMusic.value) / 100 }));
  };
  $('vsRemove').onclick = () => {
    const p = project(); if (!p?.music || !editable()) return;
    if (ctx.commit(setMusic(p, null))) toast('เอาเพลงออกแล้ว กดเลิกทำถ้าต้องการกลับไป');
  };
  const musicIn = $<HTMLInputElement>('vMusicFile');
  $('vsPick').onclick = () => { if (!editable()) return; musicIn.value = ''; musicIn.click(); };
  musicIn.onchange = async () => {
    const f = musicIn.files?.[0]; if (!f) return;
    const p = project(); if (!p) return;
    const work = ctx.saver.openedCount;
    let key = '';
    musicBusy = true;
    sayS('กำลังอ่านไฟล์เพลง…');
    try {
      if (f.size > 60 * 1024 * 1024) throw new Error('ไฟล์เพลงใหญ่เกิน 60 MB เลือกไฟล์ที่เล็กกว่านี้');
      const dur = await (await videoConvert()).probeMusic(f, f.name);
      key = newId();
      try { await putMedia(key, f, { kind: 'music', name: f.name, duration: dur }); }
      catch (e) { key = ''; throw new Error('เก็บเพลงในเครื่องไม่สำเร็จ: ' + mediaErrorText(e)); }
      void keepStorage();
      if (ctx.saver.openedCount !== work || !project()) throw new Error('งานวิดีโอที่เลือกเพลงนี้ไม่ได้เปิดอยู่แล้ว จึงยังไม่ได้ใส่เพลง');
      if (!ctx.commit(setMusic(project()!, { media: key, name: shortText(f.name, NAME_MAX_CHARS), dur, volume: p.music?.volume ?? 0.6 }))) {
        throw new Error('ใส่เพลงไม่ได้ (ดูเหตุผลที่แจ้งไว้)');
      }
      sayS(''); toast(`ใส่เพลง "${shortText(f.name, 40)}" แล้ว`);
    } catch (e) {
      sayS(errText(e));
      if (key) void deleteMedia(key).catch(() => {});   // the stored file is not used by the work
    } finally { musicBusy = false; }
  };

  /* ---------- the group's music library (0009) ---------- */
  /** a library song on this device (downloaded once, reused by every work that picks it) */
  async function libraryCopy(m: { id: string; name: string; durationMs: number }): Promise<string> {
    try {
      const have = (await listMedia()).find(x => x.kind === 'music' && x.library === m.id);
      if (have) return have.key;
    } catch (e) { throw new Error('อ่านที่เก็บเพลงในเครื่องไม่ได้: ' + mediaErrorText(e)); }
    const blob = await api.musicFile(m.id);
    const key = newId();
    try { await putMedia(key, blob, { kind: 'music', name: m.name, duration: m.durationMs / 1000, library: m.id }); }
    catch (e) { throw new Error('เก็บเพลงในเครื่องไม่สำเร็จ: ' + mediaErrorText(e)); }
    void keepStorage();
    return key;
  }
  const library = initMusicLibrary({
    isAdmin: tpl.isAdmin,
    canUpload: () => tpl.can('music_upload'),
    canAi: () => tpl.can('ai_music'),
    readonly,
    online: () => saver.mode === 'online' ? null : saver.reason,
    videoSeconds: () => { const p = project(); return p ? totalLength(p) : 0; },
    async use(m: MusicInfo) {
      const p = project(); if (!p) throw new Error('ไม่มีงานวิดีโอเปิดอยู่');
      if (readonly()) throw new Error('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อนจึงจะเปลี่ยนเพลงได้');
      if (musicBusy) throw new Error('กำลังอ่านหรือดาวน์โหลดเพลงอื่นอยู่ รอสักครู่แล้วลองใหม่');
      const work = saver.openedCount;
      musicBusy = true;
      try {
        const key = await libraryCopy(m);
        if (saver.openedCount !== work || !project()) throw new Error('งานวิดีโอที่เลือกเพลงนี้ไม่ได้เปิดอยู่แล้ว จึงยังไม่ได้ใส่เพลง');
        const now = project()!;
        if (!ctx.commit(setMusic(now, { media: key, name: shortText(m.name, NAME_MAX_CHARS), dur: m.durationMs / 1000, volume: now.music?.volume ?? 0.6, library: m.id }))) {
          throw new Error('ใส่เพลงไม่ได้ (ดูเหตุผลที่แจ้งไว้)');
        }
        toast(`ใส่เพลง "${shortText(m.name, 40)}" จากคลังแล้ว`);
      } finally { musicBusy = false; }
    }
  });
  $('vsLib').onclick = () => { if (!editable()) return; player.pause(); library.open(); };

  /**
   * A work using a library song that is not on this device (another device, a template, a shared work): fetch it
   * under the same key, once per work and song; a song deleted from the library is said, never skipped silently.
   */
  const fetched = new Set<string>();
  function fetchLibraryMusic(p: VideoProject, storeError: string | null) {
    const m = p.music;
    // someone else's work (read-only): its clips are not here either, so the song is not downloaded for nothing
    if (!m?.library || !missing.has(m.media) || storeError || saver.mode !== 'online' || readonly()) return;
    const tag = `${saver.openedCount}|${m.media}`;
    if (fetched.has(tag)) return;
    fetched.add(tag);
    const work = saver.openedCount;
    toast(`กำลังดาวน์โหลดเพลง "${shortText(m.name, 40)}" จากคลังเพลงของกลุ่ม…`);
    void (async () => {
      try {
        const blob = await api.musicFile(m.library!);
        await putMedia(m.media, blob, { kind: 'music', name: m.name, duration: m.dur, library: m.library! });
        if (saver.openedCount !== work) return;
        urls.forget(m.media);
        toast('ดาวน์โหลดเพลงจากคลังแล้ว');
        await sync();
      } catch (e) {
        // no connection: tried again at the next refresh or when it comes back; a song deleted from the library is said once
        if ((e as { status?: number }).status === 0) fetched.delete(tag);
        toast(`ดาวน์โหลดเพลง "${shortText(m.name, 40)}" ไม่ได้: ${e instanceof Error ? e.message : String(e)}`, true);
      }
    })();
  }
  window.addEventListener('online', () => { if (project()?.music?.library) void sync(); });

  /* ---------- putting missing files back from the original ---------- */
  const relinkIn = $<HTMLInputElement>('vRelinkFile');
  let relinkPending: { file: File; medias: string[] } | null = null;
  $('vRelink').onclick = () => {
    const why = importer.busy();
    if (why) { toast(why, true); return; }   // one clip at a time: two video encoders at once can run an iPhone out of memory
    relinkIn.value = ''; relinkIn.click();
  };
  relinkIn.onchange = () => { const f = relinkIn.files?.[0]; if (f) void relink(f); };
  $('vRelinkUse').onclick = () => {
    const why = importer.busy();
    if (why) { toast(why, true); return; }
    if (relinkPending) void putBack(relinkPending.file, relinkPending.medias);
  };

  const note = (t: string) => { $('vMissNote').textContent = t; };

  async function relink(file: File) {
    const p = project(); if (!p) return;
    relinkPending = null; $('vRelinkUse').hidden = true;
    const lost = [...new Map(p.clips.filter(c => missing.has(c.media)).map(c => [c.media, c])).values()];
    const musicLost = p.music && missing.has(p.music.media) ? p.music : null;
    note('กำลังอ่านไฟล์…');
    try {
      const isAudio = file.type.startsWith('audio/') || (!lost.length && !!musicLost);
      if (isAudio) {
        if (!musicLost) throw new Error('เพลงประกอบของงานนี้อยู่ในเครื่องแล้ว ไฟล์นี้ไม่จำเป็น');
        const dur = await (await videoConvert()).probeMusic(file, file.name);
        if (Math.abs(dur - musicLost.dur) > 0.3) throw new Error(`ไฟล์ "${file.name}" ยาว ${fmtTime(dur)} ไม่ตรงกับเพลงเดิม "${musicLost.name}" (ยาว ${fmtTime(musicLost.dur)})`);
        await putMedia(musicLost.media, file, { kind: 'music', name: file.name, duration: dur });
        urls.forget(musicLost.media);
        toast('ใส่เพลงกลับแล้ว');
        await sync(); return;
      }
      if (file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(file.name)) {   // a still picture of a slot: the same file name puts it back
        const photos = lost.filter(c => c.kind === 'photo' && c.name.toLowerCase() === shortText(file.name, NAME_MAX_CHARS).toLowerCase());
        if (!photos.length) throw new Error(`รูป "${file.name}" ไม่ตรงกับรูปที่หาย (${lost.filter(c => c.kind === 'photo').map(c => c.name).join(', ') || 'ไม่มีรูปที่หาย'})`);
        const pic = await preparePhoto(file, file.name);
        for (const c of photos) { await putMedia(c.media, pic.blob, { kind: 'photo', name: c.name, duration: 0, thumb: pic.thumb }); urls.forget(c.media); thumbs.delete(c.media); }
        toast(`ใส่รูปกลับแล้ว ${photos.length} ช่อง`);
        await sync(); await player.reload(); return;
      }
      const info = await (await videoConvert()).probe(file, file.name);
      const same = lost.filter(c => c.kind !== 'photo' && Math.abs(c.srcDur - info.duration) <= 0.25);
      if (!same.length) {
        throw new Error(`ไฟล์ "${file.name}" ยาว ${fmtTime(info.duration)} ไม่ตรงกับคลิปที่หาย (${lost.map(c => `${c.name} ยาว ${fmtTime(c.srcDur)}`).join(', ')}) จึงยังไม่ใช้ไฟล์นี้`);
      }
      const picked = shortText(file.name, NAME_MAX_CHARS).toLowerCase();
      const byName = same.filter(c => c.name.toLowerCase() === picked);
      if (byName.length) { await putBack(file, byName.map(c => c.media)); return; }
      relinkPending = { file, medias: same.map(c => c.media) };
      note(`ชื่อไฟล์ไม่ตรง: เลือก "${file.name}" แต่คลิปเดิมชื่อ ${same.map(c => `"${c.name}"`).join(', ')} ความยาวตรงกัน ถ้าแน่ใจว่าเป็นไฟล์เดียวกันกด "ใช้ไฟล์นี้แทน"`);
      $('vRelinkUse').hidden = false;
    } catch (e) {
      note(errText(e));
      toast(errText(e), true);
    }
  }

  /** cut the same parts again from the original and keep them under the same keys (the project does not change) */
  async function putBack(file: File, medias: string[]) {
    const p = project(); if (!p) return;
    $('vRelinkUse').hidden = true; relinkPending = null;
    ($('vRelink') as HTMLButtonElement).disabled = true;
    relinkBusy = true;
    let done = false;
    try {
      let n = 0;
      for (const key of medias) {
        const c = p.clips.find(x => x.media === key);
        if (!c) continue;
        n++;
        const copy = await (await videoConvert()).makeCopy(file, file.name, c.srcStart, c.srcEnd,
          x => note(`กำลังเตรียมคลิป ${n} จาก ${medias.length} · ${Math.round(x * 100)}%`), new AbortController().signal);
        try { await putMedia(key, copy.blob, { kind: 'clip', name: c.name, duration: copy.duration, thumb: copy.thumb }); }
        catch (e) { throw new Error('เก็บคลิปในเครื่องไม่สำเร็จ: ' + mediaErrorText(e)); }
        urls.forget(key); thumbs.delete(key);
      }
      void keepStorage();
      toast(`ใส่คลิปกลับแล้ว ${n} คลิป`);
      done = true;
    } catch (e) {
      note(errText(e));
      toast(errText(e), true);
    } finally { ($('vRelink') as HTMLButtonElement).disabled = false; relinkBusy = false; }
    // repaint only now: while the button is disabled the red bar keeps the progress text on purpose
    if (done) { await sync(); await player.reload(); }
  }

  /* ---------- V2: fades and text/pictures over the preview ---------- */
  const over = $<HTMLCanvasElement>('vOver');
  let layers: Layer[] = [];
  let layersKey = '';         // what `layers` were drawn from (frame, length, the page's objects)
  let failedKey = '';         // the last one that could not be drawn (said once, not on every refresh)
  let layerRun = 0;
  const pics: PictureCache = new Map();   // each object's picture, drawn again only when the object changes

  /** fades and the text/pictures that show at time t, over the clip on screen (same order as the export) */
  function paintOver(t: number) {
    const w = Math.round(over.clientWidth * devicePixelRatio), h = Math.round(over.clientHeight * devicePixelRatio);
    if (!w || !h) return;
    if (over.width !== w || over.height !== h) { over.width = w; over.height = h; }
    const g = over.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, w, h);
    const p = project();
    if (!p) return;
    const d = dipAt(p, t);
    drawBlack(g, d.clip, w, h);
    drawLayers(g, layers, t, { s: w / ed.doc.w, ox: 0, oy: 0 });
    drawBlack(g, d.all, w, h);
  }
  new ResizeObserver(() => paintOver(player.time)).observe(over);

  const layersKeyNow = (p: VideoProject) => `${ed.doc.w}x${ed.doc.h}|${totalLength(p)}|${history.pageJson(0)}`;

  /** the text/pictures as pictures; drawn again only when the page, the frame or the length changed */
  async function currentLayers(): Promise<Layer[]> {
    const p = project();
    if (!p) return [];
    const key = layersKeyNow(p);
    if (key === layersKey) return layers;
    const run = ++layerRun;
    const got = await renderLayers(history.pageJson(0), ed.doc, totalLength(p), pics);
    if (run === layerRun) {
      layers = got.layers; layersKey = key;
      for (const [k, pic] of pics) if (!got.used.has(k)) { if (pic) pic.img.width = pic.img.height = 0; pics.delete(k); }   // free memory early (iPhone)
    }
    return got.layers;
  }
  /** refresh for the preview; a failure is said once (and nothing stale is shown) */
  async function refreshLayers() {
    const p = project();
    if (!p) return;
    const run = layerRun + 1;   // the run currentLayers() starts (a newer one bumps layerRun past it)
    try { await currentLayers(); if (run === layerRun) failedKey = ''; }
    catch (e) {
      if (run !== layerRun || project() !== p) return;   // a newer refresh (or another work) took over
      const key = layersKeyNow(p);
      layers = []; layersKey = '';
      if (key !== failedKey) { failedKey = key; toast(errText(e), true); }
    }
    paintOver(player.time);
  }

  /* ---------- V2: text/pictures on the timeline; the chosen one gets its times from the playhead ---------- */
  const ovlRow = $('vOvl');
  let oSel: fabric.Object | null = null;
  const LANE_PX = 26, MAX_LANES = 4;
  const r3 = (x: number) => Math.round(x * 1000) / 1000;

  function overlayName(o: fabric.Object): string {
    if (isThaiText(o) || typeof (o as { text?: unknown }).text === 'string') return shortText(String((o as { text?: string }).text ?? ''), 16) || 'ข้อความ';
    if (o.type === 'image') return (o as { name?: string }).name ? shortText(String((o as { name?: string }).name), 16) : 'รูป';
    return 'รูปทรง';
  }

  function drawOverlayRow(p: VideoProject) {
    ovlRow.textContent = '';
    const total = totalLength(p);
    const objs = ed.userObjects();
    if (!objs.length) {
      const e = document.createElement('span'); e.className = 'vovempty';
      e.textContent = 'ไม่มี · กด "ข้อความ" เพื่อใส่ข้อความ โลโก้ หรือรูป';
      ovlRow.append(e);
      ovlRow.style.height = ''; ovlRow.style.width = '';
      return;
    }
    const items = objs.map((o, k) => ({ o, k, t: timingOf(o as Partial<OverlayTiming>, total) })).sort((a, b) => a.t.vStart - b.t.vStart);
    const laneEnds: number[] = [];
    let far = total;
    for (const it of items) {
      let lane = laneEnds.findIndex(e => e <= it.t.vStart + 0.001);
      if (lane < 0) { lane = Math.min(laneEnds.length, MAX_LANES - 1); if (lane === laneEnds.length) laneEnds.push(0); }
      laneEnds[lane] = Math.max(laneEnds[lane], it.t.vEnd);
      far = Math.max(far, it.t.vEnd);
      const b = document.createElement('button');
      const past = it.t.vEnd > total + 0.05;
      b.className = 'vobar' + (past ? ' vpast' : '') + (it.o === oSel ? ' vsel' : '');
      b.dataset.k = String(it.k);
      b.style.left = `${it.t.vStart * PX_PER_SECOND}px`;
      b.style.top = `${lane * LANE_PX}px`;
      b.style.width = `${Math.max(18, (it.t.vEnd - it.t.vStart) * PX_PER_SECOND - 2)}px`;
      const name = overlayName(it.o) + (it.o.visible === false ? ' (ซ่อน)' : '');
      b.textContent = name;
      b.setAttribute('aria-label', `${name} ขึ้น ${fmtTime(it.t.vStart)} ถึง ${fmtTime(it.t.vEnd)}${it.t.vFade ? ' จางเข้า-ออก' : ''}${past ? ' (เลยท้ายวิดีโอ ส่วนที่เลยจะไม่เห็น)' : ''}`);
      b.onclick = () => { oSel = oSel === it.o ? null : it.o; selected = null; markSelected(); paintOTools(); };
      ovlRow.append(b);
    }
    ovlRow.style.height = `${laneEnds.length * LANE_PX}px`;
    ovlRow.style.width = `${far * PX_PER_SECOND}px`;
  }

  /** the overlay tools replace the clip tools while a text/picture is chosen */
  function paintOTools() {
    if (oSel && !ed.userObjects().includes(oSel)) oSel = null;   // deleted, or undo rebuilt the objects
    $('vTools').hidden = !!oSel;
    $('vOTools').hidden = !oSel;
    ovlRow.querySelectorAll<HTMLElement>('.vobar').forEach(b => b.classList.toggle('vsel', !!oSel && ed.userObjects()[Number(b.dataset.k)] === oSel));
    const p = project();
    if (!oSel || !p) return;
    const t = timingOf(oSel as Partial<OverlayTiming>, totalLength(p));
    setPressed('voFade', () => t.vFade);
    $('voFadeT').textContent = t.vFade ? 'จาง: เปิด' : 'จาง: ปิด';
  }

  /** change when the chosen text/picture shows (one undo step) */
  function setTiming(change: Partial<OverlayTiming>, done: string) {
    const p = project(); if (!editable() || !p || !oSel) return;
    player.pause();
    const k = ed.userObjects().indexOf(oSel);
    if (k < 0) { oSel = null; paintOTools(); toast('ไม่พบข้อความ/รูปนี้แล้ว (อาจถูกลบหรือเลิกทำไป)', true); return; }
    const next = { ...timingOf(oSel as Partial<OverlayTiming>, totalLength(p)), ...change };
    const why = checkTiming(next);
    if (why) { toast(why, true); return; }
    if (!history.setOnPages([{ page: 0, index: k }], { ...next })) { toast('ยังแก้ไม่ได้ กำลังเปิดงานอยู่ รอสักครู่แล้วลองใหม่', true); return; }
    toast(done);
    void sync();
  }
  const chosen = () => { const p = project(); return p && oSel ? timingOf(oSel as Partial<OverlayTiming>, totalLength(p)) : null; };
  $('voStart').onclick = () => {
    const t = chosen(); if (!t) return;
    const at = r3(player.time);
    if (at > t.vEnd - OVERLAY_MIN_SECONDS) { toast(`จุดเริ่ม (${fmtTime(at)}) ต้องอยู่ก่อนจุดจบ (${fmtTime(t.vEnd)}) อย่างน้อย ${OVERLAY_MIN_SECONDS} วิ เลื่อนเส้นเวลาไปทางซ้าย หรือกด "จบตรงนี้" ก่อน`, true); return; }
    setTiming({ vStart: at }, `ตั้งให้เริ่มขึ้นที่ ${fmtTime(at)} แล้ว`);
  };
  $('voEnd').onclick = () => {
    const t = chosen(); if (!t) return;
    const at = r3(player.time);
    if (at < t.vStart + OVERLAY_MIN_SECONDS) { toast(`จุดจบ (${fmtTime(at)}) ต้องอยู่หลังจุดเริ่ม (${fmtTime(t.vStart)}) อย่างน้อย ${OVERLAY_MIN_SECONDS} วิ เลื่อนเส้นเวลาไปทางขวา หรือกด "เริ่มตรงนี้" ก่อน`, true); return; }
    setTiming({ vEnd: at }, `ตั้งให้หายไปที่ ${fmtTime(at)} แล้ว`);
  };
  $('voAll').onclick = () => {
    const p = project(); if (!p) return;
    const total = totalLength(p);
    if (total < OVERLAY_MIN_SECONDS) { toast('ยังไม่มีคลิป เพิ่มคลิปก่อนจึงจะตั้งให้ขึ้นทั้งวิดีโอได้', true); return; }
    setTiming({ vStart: 0, vEnd: r3(total) }, `ตั้งให้ขึ้นตลอดทั้งวิดีโอ (${fmtTime(total)}) แล้ว`);
  };
  $('voFade').onclick = () => {
    const t = chosen(); if (!t) return;
    setTiming({ vFade: !t.vFade }, t.vFade ? 'ปิดการจางแล้ว (โผล่และหายทันที)' : 'เปิดการจางเข้า-ออกแล้ว');
  };
  $('voEdit').onclick = () => { if (oSel) void enterEdit(oSel); };
  $('voDel').onclick = () => {
    if (!editable() || !oSel) return;
    if (!ed.userObjects().includes(oSel)) { oSel = null; paintOTools(); return; }
    player.pause();
    const name = overlayName(oSel);
    ed.canvas.remove(oSel);   // recorded like any edit (object:removed)
    oSel = null;
    toast(`ลบ "${name}" แล้ว กดเลิกทำถ้าต้องการกลับไป`);
    void sync();
  };
  $('voBack').onclick = () => { oSel = null; paintOTools(); };

  /* ---------- V2: editing the text/pictures on the design screen, over a still of the video ---------- */
  let editing = false;
  let editAt = 0;   // the video time the still shows; objects added now start here

  /** the picture at the playhead, filling the work's frame like the preview (a plain colour when there is none) */
  function still(p: VideoProject): fabric.Pattern | string {
    const v = player.picture();
    if (!v) return '#000000';
    const c = document.createElement('canvas');
    c.width = ed.doc.w; c.height = ed.doc.h;
    const g = c.getContext('2d');
    if (!g) return '#000000';
    const s = Math.max(c.width / v.w, c.height / v.h) * v.zoom;   // a still picture keeps its zoom at this moment
    const w = v.w * s, h = v.h * s;
    try { g.drawImage(v.source, (c.width - w) / 2, (c.height - h) / 2, w, h); }
    catch (e) { toast('แสดงภาพนิ่งของวิดีโอไม่ได้ (ยังแก้ข้อความได้): ' + errText(e), true); return '#000000'; }
    drawBlack(g, dipAt(p, editAt).clip, c.width, c.height);
    return new fabric.Pattern({ source: c as unknown as HTMLImageElement, repeat: 'no-repeat' });   // Fabric 5 draws a canvas source too
  }

  /** after undo/redo while editing: the still and its time follow the work */
  function refreshStill(p: VideoProject) {
    editAt = player.time;
    ed.board.set('fill', still(p));
    $('vedText').textContent = editText();
    ed.canvas.requestRenderAll();
  }
  const editText = () => `ภาพที่ ${fmtTime(editAt)} · ชิ้นใหม่ขึ้นตั้งแต่ตรงนี้ ${OVERLAY_NEW_SECONDS} วิ · ชิ้นที่จางคือไม่ขึ้นตอนนี้`;

  async function enterEdit(focus?: fabric.Object) {
    const p = project(); if (!p) return;
    if (!editable()) return;
    player.pause();
    if (focus) {   // show it: move the playhead into its time
      const t = timingOf(focus as Partial<OverlayTiming>, totalLength(p));
      if (player.time < t.vStart || player.time >= t.vEnd) await player.seek(Math.min(t.vStart + 0.05, Math.max(0, totalLength(p) - 0.05)));
    }
    if (project() !== p) return;   // the work changed while moving
    closeSheets();
    editAt = player.time;
    ed.board.set('fill', still(p));
    editing = true;
    app.classList.add('vedit');
    view.hidden = true;
    $('vedBar').hidden = false;
    $('vedText').textContent = editText();
    ed.fit();
    // the design tabs came back under the work area: fit again once the canvas has its new size
    requestAnimationFrame(() => requestAnimationFrame(() => { if (editing) ed.fit(); }));
    if (focus && ed.userObjects().includes(focus)) ed.canvas.setActiveObject(focus);
    ed.canvas.requestRenderAll();
  }
  /** back to the video screen without redrawing it (the caller does) */
  function stopEditing() {
    if (!editing) return;
    editing = false;
    ed.canvas.discardActiveObject();
    ed.board.set('fill', ed.doc.bg);
    app.classList.remove('vedit');
    $('vedBar').hidden = true;
    ed.canvas.requestRenderAll();
  }
  function leaveEdit() {
    if (!editing) return;
    stopEditing();
    closeSheets();
    view.hidden = !project();
    void sync();
  }
  $('vText').onclick = () => void enterEdit();
  $('vedDone').onclick = leaveEdit;

  // while editing: objects that do not show at the still's time are drawn faint (the chosen ones in full).
  // Fabric 5.3 draws a canvas's objects in _renderObjects; nothing is changed on the objects themselves.
  const fc = ed.canvas as unknown as { _renderObjects: (ctx: CanvasRenderingContext2D, objs: fabric.Object[]) => void };
  const plainRender = fc._renderObjects;
  fc._renderObjects = function (this: fabric.Canvas, g: CanvasRenderingContext2D, objs: fabric.Object[]) {
    const p = project();
    if (!editing || !p) { plainRender.call(this, g, objs); return; }
    const total = totalLength(p), chosenNow = new Set(ed.canvas.getActiveObjects());
    for (const o of objs) {
      if (!o) continue;
      const t = o === ed.board || chosenNow.has(o) ? null : timingOf(o as Partial<OverlayTiming>, total);
      if (t && (editAt < t.vStart || editAt >= t.vEnd)) { g.save(); g.globalAlpha = 0.25; o.render(g); g.restore(); }
      else o.render(g);
    }
  };
  // something added while editing starts at the still's time, for 3 s (not objects coming back from undo or a load)
  ed.canvas.on('object:added', e => {
    const o = e.target as (fabric.Object & Partial<OverlayTiming>) | undefined;
    const p = project();
    if (!editing || !o || !p || o === ed.board || history.loading || typeof o.vStart === 'number') return;
    o.set(newTiming(editAt, totalLength(p)) as Partial<fabric.Object>);
  });

  /* ---------- V2: จางหาย ---------- */
  const fIn = $in('vfIn'), fOut = $in('vfOut'), fJoint = $in('vfJoint');
  let fadeClip: string | null = null;   // the clip the joint sheet is about (the joint before it)
  const sayF = (t: string) => { $('vfMsg').textContent = t; $('vfMsg').hidden = !t; };
  /** index of the later clip of the joint shown (≥ 1), or -1 with fewer than 2 clips */
  function jointIndex(p: VideoProject): number {
    if (p.clips.length < 2) return -1;
    const k = fadeClip ? p.clips.findIndex(c => c.id === fadeClip) : -1;
    return Math.max(1, k);
  }
  function paintFade() {
    const p = project(); if (!p) return;
    fIn.checked = p.fadeIn; fOut.checked = p.fadeOut;
    const j = jointIndex(p);
    fJoint.checked = j > 0 && p.clips[j].fade;
    $('vfJointText').textContent = j > 0 ? `จางผ่านสีดำ ระหว่างคลิปที่ ${j} กับ ${j + 1}` : 'ต้องมีอย่างน้อย 2 คลิปจึงจะมีรอยต่อ';
    const ro = readonly();
    fIn.disabled = fOut.disabled = ro;
    fJoint.disabled = ro || j < 0;
    ($('vfAll') as HTMLButtonElement).disabled = ($('vfNone') as HTMLButtonElement).disabled = ro || j < 0;
  }
  function fadeEdit(e: Edit, done: string) {
    sayF('');
    if (!e.ok) sayF(e.error);
    else if (ctx.commit(e)) toast(done);
    else sayF('แก้ไม่ได้ (ดูเหตุผลที่แจ้งไว้)');
    paintFade();   // a refused change puts the box back as it is in the work
  }
  $('vFade').onclick = () => {
    const p = project(); if (!p) return;
    player.pause();
    const k = target();
    fadeClip = k >= 0 ? p.clips[k].id : null;
    sayF(''); paintFade(); openSheet('shVFade');
  };
  fIn.onchange = () => { const p = project(); if (p) fadeEdit(setEndFades(p, { fadeIn: fIn.checked }), fIn.checked ? 'ตั้งให้จางเข้าตอนเริ่มแล้ว' : 'เอาการจางตอนเริ่มออกแล้ว'); };
  fOut.onchange = () => { const p = project(); if (p) fadeEdit(setEndFades(p, { fadeOut: fOut.checked }), fOut.checked ? 'ตั้งให้จางออกตอนจบแล้ว' : 'เอาการจางตอนจบออกแล้ว'); };
  fJoint.onchange = () => {
    const p = project(); if (!p) return;
    const j = jointIndex(p);
    fadeEdit(setJointFade(p, j, fJoint.checked), fJoint.checked ? `ใส่การจางระหว่างคลิปที่ ${j} กับ ${j + 1} แล้ว` : `เอาการจางระหว่างคลิปที่ ${j} กับ ${j + 1} ออกแล้ว`);
  };
  $('vfAll').onclick = () => { const p = project(); if (p) fadeEdit(setJointFade(p, 'all', true), `ใส่การจางทุกรอยต่อแล้ว (${p.clips.length - 1} รอยต่อ)`); };
  $('vfNone').onclick = () => { const p = project(); if (p) fadeEdit(setJointFade(p, 'all', false), 'เอาการจางที่รอยต่อออกหมดแล้ว'); };

  /* ---------- import + export ---------- */
  const importer = initVideoImport(ctx, () => relinkBusy ? 'กำลังใส่คลิปกลับจากไฟล์ต้นฉบับอยู่ รอให้เสร็จก่อน' : null);
  const exporter = initVideoExport(ctx);
  // เสียงพากย์ AI (0016)
  const voice = initVideoVoice(ctx, { isAdmin: tpl.isAdmin, pause: () => player.pause() });
  $('vVoice').onclick = () => { if (!project()) return; voice.open(target()); };

  /* ---------- เทมเพลตวิดีโอ: slots, texts, template picture ---------- */
  const slots = initVideoSlots(ctx, {
    history, fillSlots: importer.fillSlots, fillOne: importer.fillOne, thumbOf: k => thumbs.get(k) ?? '',
    canPublish: tpl.canPublish, publish: () => { player.pause(); tpl.publish(); }, refresh: () => void sync()
  });
  function paintSlotsBtn(p: VideoProject) {
    const empty = emptySlots(p);
    const b = $('vSlotsBtn');
    b.textContent = empty ? `ช่องว่าง ${empty}` : 'ช่องคลิป';
    b.classList.toggle('vslotsneed', empty > 0);
  }
  $('vSlotsBtn').onclick = () => { player.pause(); slots.open(); };

  /** the picture at the playhead with its text and fades, as a JPEG data URL (a template's preview picture) */
  async function poster(maxSide = 480): Promise<string> {
    const p = project(); if (!p) throw new Error('ไม่มีงานวิดีโอเปิดอยู่');
    const ls = await currentLayers();
    const s = Math.min(1, maxSide / Math.max(ed.doc.w, ed.doc.h));
    const c = document.createElement('canvas');
    c.width = Math.round(ed.doc.w * s); c.height = Math.round(ed.doc.h * s);
    const g = c.getContext('2d'); if (!g) throw new Error('เครื่องนี้วาดภาพไม่ได้');
    g.fillStyle = '#000'; g.fillRect(0, 0, c.width, c.height);
    const v = player.picture();
    if (v) { const k = Math.max(c.width / v.w, c.height / v.h) * v.zoom; g.drawImage(v.source, (c.width - v.w * k) / 2, (c.height - v.h * k) / 2, v.w * k, v.h * k); }
    const d = dipAt(p, player.time);
    drawBlack(g, d.clip, c.width, c.height);
    drawLayers(g, ls, player.time, { s, ox: 0, oy: 0 });
    drawBlack(g, d.all, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.85);
  }

  return {
    sync,
    openExport: () => { leaveEdit(); player.pause(); exporter.open(); },
    /** a reason not to open another work now (a clip is being prepared or a video written), or null */
    busy: (): string | null => importer.busy()
      ?? (exporter.busy ? 'กำลังสร้างวิดีโออยู่ รอให้เสร็จหรือกดหยุดก่อน' : null)
      ?? (relinkBusy ? 'กำลังใส่คลิปกลับจากไฟล์ต้นฉบับอยู่ รอให้เสร็จก่อน' : null)
      ?? (musicBusy ? 'กำลังอ่านไฟล์เพลงอยู่ รอสักครู่' : null),
    get active() { return !!project(); },
    /** "ช่องคลิป" (e.g. right after starting a work from a video template) */
    openSlots: () => { player.pause(); slots.open(); },
    poster,
    /** the last MP4 written for this work (a template's preview), or null */
    lastExport: () => exporter.lastFile
  };
}

export type VideoView = ReturnType<typeof initVideoView>;
